import { useEscapeClose } from '@/shared/hooks/useEscapeClose';
import { useState } from 'react';
import { useRefetchOnOpen } from '@/shared/hooks/useRefetchOnOpen';
import { useClosingReasons } from '../hooks';

interface Props {
  open: boolean;
  onClose: () => void;
  /** `reasonId` undefined = encerrar sem motivo (só quando o canal permite). */
  onConfirm: (reasonId: number | undefined, notes?: string) => Promise<void> | void;
  /**
   * Canal exige motivo? Ausente = exige (comportamento de sempre). Quando não
   * exige, o botão libera sem seleção e o atendimento é registrado com o
   * motivo de sistema "Não especificado".
   */
  exigeMotivo?: boolean;
}

/**
 * Story 3.10 — modal de fechamento (referência: 12-modal-fechar.html).
 */
export function CloseModal({ open, onClose, onConfirm, exigeMotivo = true }: Props) {
  const reasonsQ = useClosingReasons();
  // Abrir o modal busca os motivos — mesmo gatilho do `/` e do popover de
  // etiquetas. O componente fica sempre montado (recebe `open` como prop),
  // por isso a transição e não a montagem.
  useRefetchOnOpen(open, reasonsQ.refetch);
  const [selected, setSelected] = useState<number | null>(null);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // ESC fecha (listener no document). O onKeyDown do overlay só disparava com o
  // div focado — por isso o ESC não fechava.
  useEscapeClose(open, onClose);

  if (!open) return null;

  const submit = async (): Promise<void> => {
    if ((exigeMotivo && selected === null) || submitting) return;
    setSubmitting(true);
    try {
      await onConfirm(selected ?? undefined, notes.trim() || undefined);
      setSelected(null);
      setNotes('');
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      onKeyDown={(e) => e.key === 'Escape' && onClose()}
    >
      <div
        className="w-[440px] max-w-[90vw] rounded-md border border-n-200 bg-n-0 p-6 shadow-lg"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <h2 className="font-bold text-[16px] text-n-900">Fechar atendimento</h2>
        <p className="mt-1 text-[13px] text-n-500">
          {exigeMotivo
            ? 'Selecione o motivo do fechamento. Esse dado vai pra relatórios.'
            : 'O motivo é opcional neste canal. Sem escolher, o atendimento entra no relatório como “Não especificado”.'}
        </p>

        <div className="mt-4">
          <label htmlFor="closing-reason-select" className="font-semibold text-[12px] text-n-700">
            {exigeMotivo ? 'Motivo' : 'Motivo (opcional)'}
          </label>
          <select
            id="closing-reason-select"
            value={selected ?? ''}
            onChange={(e) => setSelected(e.target.value ? Number(e.target.value) : null)}
            className="mt-1 w-full rounded-md border border-n-200 px-3 py-2 text-[13px] text-n-900 outline-none focus:border-brand-500 bg-n-0"
          >
            <option value="">
              {exigeMotivo ? 'Selecione um motivo…' : 'Sem motivo específico'}
            </option>
            {(reasonsQ.data ?? [])
              // Motivo de sistema ("Não especificado") não entra na lista: ele é
              // o que o fechamento SEM motivo grava, não algo pra escolher.
              .filter((r) => r.active && !r.systemKey)
              .map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
          </select>
        </div>

        <div className="mt-4">
          <label htmlFor="close-notes" className="font-semibold text-[12px] text-n-700">
            Notas (opcional)
          </label>
          <textarea
            id="close-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            maxLength={2000}
            className="mt-1 w-full resize-none rounded-md border border-n-200 px-3 py-2 text-[13px] outline-none focus:border-brand-500"
          />
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-n-300 bg-n-0 px-4 py-2 font-bold text-[12px] text-n-700 uppercase tracking-[0.05em] hover:bg-n-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={(exigeMotivo && selected === null) || submitting}
            onClick={() => void submit()}
            className="rounded-md bg-danger-500 px-4 py-2 font-bold text-[12px] text-white uppercase tracking-[0.05em] hover:bg-danger-600 disabled:opacity-50"
          >
            {submitting ? 'Fechando…' : 'Fechar atendimento'}
          </button>
        </div>
      </div>
    </div>
  );
}
